import canonical from './generated/manifest.json';
import type {Launch} from './generated/wire';
import {validators} from './generated/validators.js';
const validManifest=validators.GameManifest!,validSchema=validators.ManifestSchema!;
const recursiveRef='#/definitions/jsonSchema';
type Step={property:string}|{values:'array'|'object';except?:string[]};
type Schema=Record<string,any>;
/** Discover recursive DATA positions in the trusted schema, not in developer metadata. */
function positions(schema:Schema,path:Step[]=[],ancestors=new Set<unknown>()):Step[][] {
 if(!schema||typeof schema!=='object')return [];
 if(schema.$ref===recursiveRef)return [path];
 if(ancestors.has(schema))throw Error('unsupported_canonical_recursion');
 const next=new Set(ancestors).add(schema);
 if(schema.$ref){
  if(!schema.$ref.startsWith('#/'))throw Error('unsupported_canonical_reference');
  const target=schema.$ref.slice(2).split('/').reduce((value:any,key:string)=>value[key.replace(/~1/g,'/').replace(/~0/g,'~')],canonical);
  return positions(target,path,next);
 }
 const found:Step[][]=[];
 for(const [property,child] of Object.entries(schema.properties??{}))found.push(...positions(child as Schema,[...path,{property}],next));
 if(schema.additionalProperties)found.push(...positions(schema.additionalProperties,[...path,{values:'object',except:Object.keys(schema.properties??{})}],next));
 if(schema.items)found.push(...positions(schema.items,[...path,{values:'array'}],next));
 for(const keyword of ['allOf','anyOf','oneOf'])for(const child of schema[keyword]??[])found.push(...positions(child,path,next));
 return found;
}
const rootPositions=positions(canonical);
const schemaPositions=positions(canonical.definitions.jsonSchema);
function appendChildren(value:unknown,paths:Step[][],pending:unknown[]) {
 for(const path of paths){
  let values=[value];
  for(const step of path){
   const children:unknown[]=[];
   for(const node of values){
    if('property' in step){if(node&&typeof node==='object'&&Object.hasOwn(node,step.property))children.push((node as Schema)[step.property]);}
    else if(step.values==='array'){if(Array.isArray(node))for(const child of node)children.push(child);}
    else if(node&&typeof node==='object'&&!Array.isArray(node))for(const [key,child] of Object.entries(node))if(!step.except?.includes(key))children.push(child);
   }
   values=children;
  }
  // The canonical `items`/`dependencies` alternatives also permit arrays. Those
  // have their own discovered positions; only object/boolean values are schemas.
  for(const node of values)if(typeof node==='boolean'||node&&typeof node==='object'&&!Array.isArray(node))pending.push(node);
 }
}
/** Equality of parsed JSON values only; object key order is immaterial, array order is not. */
function equalJson(left:unknown,right:unknown):boolean {
 const pending:[unknown,unknown][]=[[left,right]];
 while(pending.length){
  const [a,b]=pending.pop()!;if(a===b)continue;
  if(!a||!b||typeof a!=='object'||typeof b!=='object')return false;
  if(Array.isArray(a)!==Array.isArray(b))return false;
  const keys=Object.keys(a);if(keys.length!==Object.keys(b).length)return false;
  for(const key of keys){if(!Object.hasOwn(b,key))return false;pending.push([(a as Schema)[key],(b as Schema)[key]]);}
 }
 return true;
}
function uniqueEnum(values:unknown[]):boolean {
 for(let i=0;i<values.length;i++)for(let j=0;j<i;j++)if(equalJson(values[i],values[j]))return false;
 return true;
}
/** Check real schema edges and local reference edges once, without expanding a DAG.
 * Metadata stays inert unless a genuine reference explicitly targets it. */
function validateEmbedded(root:unknown) {
 const states=new Map<unknown,number>();
 const frames:{node:unknown;exit:boolean}[]=[{node:root,exit:false}];
 while(frames.length){
  const {node,exit}=frames.pop()!;
  if(exit){states.set(node,2);continue;}
  if(states.get(node)===1)throw Error('cyclic_schema_reference');
  if(states.get(node)===2)continue;
  if(!validSchema(node))throw Error('invalid_body');
  if(node&&typeof node==='object'&&Array.isArray((node as Schema).enum)&&!uniqueEnum((node as Schema).enum))throw Error('invalid_body');
  states.set(node,1);frames.push({node,exit:true});
  const children:unknown[]=[];appendChildren(node,schemaPositions,children);
  if(node&&typeof node==='object'&&Object.hasOwn(node,'$ref')){
   const ref=(node as Schema).$ref;
   if(typeof ref!=='string'||(ref!=='#'&&!ref.startsWith('#/')))throw Error('external_schema_reference');
   let target:any=root;
   if(ref!=='#'){
    let pointer:string;try{pointer=decodeURIComponent(ref.slice(2));}catch{throw Error('invalid_schema_reference');}
    for(const part of pointer.split('/')){
     if(/~(?:[^01]|$)/.test(part))throw Error('invalid_schema_reference');
     const token=part.replace(/~1/g,'/').replace(/~0/g,'~');
     if(!target||typeof target!=='object'||!Object.hasOwn(target,token))throw Error('invalid_schema_reference');
     target=target[token];
    }
   }
   children.push(target);
  }
  for(const child of children)frames.push({node:child,exit:false});
 }
}
/** Bytes bound the JSON tree; no undocumented node/depth profile or developer compilation. */
export function validateManifest(value:unknown):Launch['manifest'] {
 if(!validManifest(value))throw Error('invalid_body');
 const manifest=value as {hostAudio?:{assetIds:string[]};assets:{path:string}[]};
 if(manifest.hostAudio){const paths=new Set(manifest.assets.map(a=>a.path));if(manifest.hostAudio.assetIds.some(id=>!paths.has(id)))throw Error('unknown_host_audio_asset');}
 const roots:unknown[]=[];appendChildren(value,rootPositions,roots);
 for(const root of roots)validateEmbedded(root);
 return value as unknown as Launch['manifest'];
}
